import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { systemStatusService, supportTaskService } from '../api';
import { Button, ThemedText } from '../components';
import { SentryLevelColors, SentryIssueStatusColors, StatusPalette, withOpacity } from '../constants/statusColors';
import { PageContainer } from '../components/PageStyles';
import { Card } from '../components/Card';
import { ChevronRight } from 'lucide-react';

// ─── Styled Components ───────────────────────────────────────────────────────

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 28px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.primary || props.theme.colors.tint};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: ${props => props.theme.borderRadius.md};
  opacity: 0.8;
  transition: all 0.2s;
  margin-left: -${props => props.theme.spacing.sm};

  &:hover {
    opacity: 1;
    background-color: ${props => props.theme.colors.primary}10;
  }
`;

const HeaderTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const HeaderTitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex: 1;
  min-width: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;


const CardTitle = styled.h3`
  margin: 0 0 ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.large};
`;

const PublicUrlContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PublicUrlInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};
  font-family: monospace;
  min-width: 0;
  cursor: text;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const CopyButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$copied ? props.theme.colors.tint + '20' : props.theme.colors.backgroundSecondary};
  color: ${props => props.$copied ? props.theme.colors.tint : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.tint}15;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${props => props.theme.colors.muted};
`;

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${props => props.theme.spacing.lg};
`;

// ─── Error Card Styles ───────────────────────────────────────────────────────

const ErrorTitle = styled.h2`
  margin: 0 0 ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.xlarge || '20px'};
  word-break: break-word;
`;

const Culprit = styled.div`
  font-family: monospace;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.textSecondary || props.theme.colors.muted};
  margin-bottom: ${props => props.theme.spacing.md};
  word-break: break-all;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const IssueLevelBadge = styled.span`
  padding: 4px 10px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  ${props => {
    const c = SentryLevelColors[props.$level] || StatusPalette.slate;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const ProjectBadge = styled.span`
  padding: 4px 10px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.theme.colors.tint + '20'};
  color: ${props => props.theme.colors.tint};
`;

const ErrorMessage = styled.div`
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: monospace;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.text};
  white-space: pre-wrap;
  word-break: break-word;
`;

// ─── Stacktrace Styles ───────────────────────────────────────────────────────

const FrameContainer = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  overflow: hidden;
`;

const FrameHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.$inApp ? props.theme.colors.tint + '10' : props.theme.colors.background};
  cursor: pointer;
  font-family: monospace;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.text};

  &:hover {
    background-color: ${props => props.theme.colors.tint + '20'};
  }
`;

const FrameToggle = styled.span`
  font-size: 10px;
  color: ${props => props.theme.colors.muted};
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const FrameFileName = styled.span`
  color: ${props => props.$inApp ? props.theme.colors.tint : props.theme.colors.muted};
  font-weight: ${props => props.$inApp ? 600 : 400};
`;

const FrameFunction = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const InAppBadge = styled.span`
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  background-color: ${props => props.theme.colors.tint + '20'};
  color: ${props => props.theme.colors.tint};
  flex-shrink: 0;
`;

const FrameCode = styled.pre`
  margin: 0;
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  font-family: monospace;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const CodeLine = styled.div`
  display: flex;
  background-color: ${props => props.$highlighted ? props.theme.colors.tint + '15' : 'transparent'};
`;

const LineNumber = styled.span`
  color: ${props => props.theme.colors.textSecondary || props.theme.colors.muted};
  min-width: 50px;
  text-align: right;
  padding-right: ${props => props.theme.spacing.md};
  user-select: none;
`;

const LineCode = styled.span`
  color: ${props => props.theme.colors.text};
  white-space: pre;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

// ─── Breadcrumbs Styles ──────────────────────────────────────────────────────

const BreadcrumbTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${props => props.theme.typography.sizes.small};
`;

const BreadcrumbRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const BreadcrumbCell = styled.td`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  vertical-align: top;
`;

const BreadcrumbHeaderCell = styled.th`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.muted};
  font-weight: 600;
  text-align: left;
  font-size: ${props => props.theme.typography.sizes.small};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const LevelBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  ${props => {
    const c = SentryLevelColors[props.$level] || StatusPalette.slate;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

// ─── Sidebar Styles ──────────────────────────────────────────────────────────

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${props => props.theme.spacing.xs} 0;
  gap: ${props => props.theme.spacing.md};
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  text-align: right;
  word-break: break-word;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  ${props => {
    const c = SentryIssueStatusColors[props.$status] || StatusPalette.blue;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const TagRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${props => props.theme.spacing.xs} 0;
  gap: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border}40;

  &:last-child {
    border-bottom: none;
  }
`;

const TagKey = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
`;

const TagValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: 12px;
  text-align: right;
  word-break: break-all;
`;

// ─── Actions / AI Analysis Styles ────────────────────────────────────────────

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.tint + '10'};
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.theme.colors.tint};
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$variant === 'primary'
    ? props.theme.colors.tint
    : props.theme.colors.background};
  color: ${props => props.$variant === 'primary'
    ? '#ffffff'
    : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.6 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  &:hover {
    opacity: 0.85;
    background-color: ${props => props.$variant === 'primary'
      ? props.theme.colors.tint
      : props.theme.colors.tint + '10'};
    border-color: ${props => props.theme.colors.tint};
  }
`;

const AnalysisSection = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const AnalysisLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const AnalysisText = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  line-height: 1.5;
  margin-bottom: ${props => props.theme.spacing.md};
  white-space: pre-wrap;
`;

const AnalysisBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-right: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.xs};
  background-color: ${props => props.$color || props.theme.colors.tint}20;
  color: ${props => props.$color || props.theme.colors.tint};
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return dateString;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

const SentryIssueDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { issueId } = useParams();
  const { isSuperAdmin } = useRole();

  const [issue, setIssue] = useState(null);
  const [latestEvent, setLatestEvent] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [publicUrlCopied, setPublicUrlCopied] = useState(false);

  // Collapsible frame state: map of frame index -> expanded boolean
  const [expandedFrames, setExpandedFrames] = useState({});

  const publicUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/public/sentry/${issueId}?includeStacktrace=true&includeBreadcrumbs=true`;

  const handleCopyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setPublicUrlCopied(true);
      setTimeout(() => setPublicUrlCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [issueRes, eventRes] = await Promise.all([
          systemStatusService.getSentryIssueDetails(issueId),
          systemStatusService.getSentryIssueLatestEvent(issueId),
        ]);
        setIssue(issueRes.data || issueRes);
        setLatestEvent(eventRes.data || eventRes);
      } catch (err) {
        toast.error('Failed to load Sentry issue details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [issueId]);

  // Initialize expanded frames when latestEvent loads
  useEffect(() => {
    if (latestEvent) {
      const exceptionData = getExceptionData();
      if (exceptionData?.stacktrace?.frames) {
        const frames = [...exceptionData.stacktrace.frames].reverse();
        const initial = {};
        frames.forEach((frame, idx) => {
          initial[idx] = !!frame.inApp;
        });
        setExpandedFrames(initial);
      }
    }
  }, [latestEvent]);

  const getExceptionData = () => {
    if (!latestEvent?.entries) return null;
    const exceptionEntry = latestEvent.entries.find(e => e.type === 'exception');
    if (!exceptionEntry) return null;
    return exceptionEntry.data?.values?.[0];
  };

  const getBreadcrumbs = () => {
    if (!latestEvent?.entries) return [];
    const breadcrumbEntry = latestEvent.entries.find(e => e.type === 'breadcrumbs');
    if (!breadcrumbEntry) return [];
    const values = breadcrumbEntry.data?.values || [];
    // Return last 25, most recent first
    return [...values].reverse().slice(0, 25);
  };

  const getErrorMessage = () => {
    const exceptionData = getExceptionData();
    if (exceptionData?.value) return exceptionData.value;
    if (latestEvent?.metadata?.value) return latestEvent.metadata.value;
    if (issue?.metadata?.value) return issue?.metadata?.value;
    return null;
  };

  const toggleFrame = (index) => {
    setExpandedFrames(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      const result = await supportTaskService.analyzeSentryIssue(issueId);
      setAiAnalysis(result.data || result);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error('Failed to analyze issue');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCreateTask = () => {
    navigate(`/tasks/new?sentryIssueId=${issueId}&sentryTitle=${encodeURIComponent(issue.title)}&sentryUrl=${encodeURIComponent(issue.permalink)}`);
  };

  const handleCreateTaskFromAnalysis = () => {
    navigate(`/tasks/new?sentryIssueId=${issueId}&sentryTitle=${encodeURIComponent(issue?.title || '')}&sentryUrl=${encodeURIComponent(issue?.permalink || '')}`, {
      state: { aiAnalysis: aiAnalysis },
    });
  };

  const handleUpdateStatus = async (status) => {
    setUpdatingStatus(status);
    try {
      await systemStatusService.updateSentryIssueStatus(issueId, status);
      setIssue(prev => ({ ...prev, status }));
      toast.success(`Issue ${status}`);
    } catch {
      toast.error('Failed to update issue status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // ─── Loading State ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout currentPath="/system-status">
        <LoadingContainer theme={theme}>
          <ThemedText>Loading Sentry issue details...</ThemedText>
        </LoadingContainer>
      </Layout>
    );
  }

  // ─── Not Found State ───────────────────────────────────────────────────────

  if (!issue) {
    return (
      <Layout currentPath="/system-status">
        <PageContainer theme={theme}>
          <NotFoundContainer theme={theme}>
            <ThemedText type="title">Issue Not Found</ThemedText>
            <ThemedText style={{ color: theme.colors.muted }}>
              The Sentry issue you are looking for could not be loaded or does not exist.
            </ThemedText>
            <Button onClick={handleBack}>
              Go Back
            </Button>
          </NotFoundContainer>
        </PageContainer>
      </Layout>
    );
  }

  // ─── Data Extraction ───────────────────────────────────────────────────────

  const exceptionData = getExceptionData();
  const frames = exceptionData?.stacktrace?.frames
    ? [...exceptionData.stacktrace.frames].reverse()
    : [];
  const breadcrumbs = getBreadcrumbs();
  const errorMessage = getErrorMessage();
  const tags = latestEvent?.tags || [];
  const contexts = latestEvent?.contexts || {};

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout currentPath="/system-status">
      <PageContainer theme={theme}>
        {/* Header */}
        <HeaderSection theme={theme}>
          <HeaderTopRow theme={theme}>
            <BackButton theme={theme} onClick={handleBack}>
              &larr; Back
            </BackButton>
          </HeaderTopRow>

          <HeaderTitleRow theme={theme}>
            <HeaderTitleContent theme={theme}>
              <ThemedText type="title" style={{ lineHeight: '1', fontSize: '22px' }}>
                Sentry Issue
              </ThemedText>
              <ThemedText style={{ color: theme.colors.muted, fontSize: '13px', fontWeight: '500' }}>
                | {issueId}
              </ThemedText>
            </HeaderTitleContent>
          </HeaderTitleRow>
        </HeaderSection>

        <ContentGrid theme={theme}>
          {/* ─── Main Content ──────────────────────────────────────────── */}
          <MainContent theme={theme}>

            {/* Error Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Error</CardTitle>

              <BadgeRow theme={theme}>
                <IssueLevelBadge theme={theme} $level={issue.level}>
                  {issue.level || 'error'}
                </IssueLevelBadge>
                {(issue.project?.name || issue.project?.slug) && (
                  <ProjectBadge theme={theme}>
                    {issue.project?.name || issue.project?.slug}
                  </ProjectBadge>
                )}
              </BadgeRow>

              <ErrorTitle theme={theme}>{issue.title}</ErrorTitle>

              {issue.culprit && (
                <Culprit theme={theme}>{issue.culprit}</Culprit>
              )}

              {errorMessage && (
                <ErrorMessage theme={theme}>{errorMessage}</ErrorMessage>
              )}
            </Card>

            {/* Stacktrace Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Stacktrace</CardTitle>

              {frames.length === 0 ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>
                    No stacktrace available
                  </ThemedText>
                </EmptyState>
              ) : (
                frames.map((frame, idx) => {
                  const isExpanded = !!expandedFrames[idx];
                  const contextLines = frame.context || [];

                  return (
                    <FrameContainer key={idx} theme={theme}>
                      <FrameHeader
                        theme={theme}
                        $inApp={!!frame.inApp}
                        onClick={() => toggleFrame(idx)}
                      >
                        <FrameToggle theme={theme} $expanded={isExpanded}>
                          <ChevronRight size={12} />
                        </FrameToggle>
                        <FrameFileName theme={theme} $inApp={!!frame.inApp}>
                          {frame.filename || frame.absPath || '(unknown)'}
                          {frame.lineNo != null ? `:${frame.lineNo}` : ''}
                          {frame.colNo != null ? `:${frame.colNo}` : ''}
                        </FrameFileName>
                        <span style={{ color: theme.colors.muted }}>in</span>
                        <FrameFunction theme={theme}>
                          {frame.function || '(anonymous)'}
                        </FrameFunction>
                        {frame.inApp && (
                          <InAppBadge theme={theme}>app</InAppBadge>
                        )}
                      </FrameHeader>

                      {isExpanded && contextLines.length > 0 && (
                        <FrameCode theme={theme}>
                          {contextLines.map((contextLine, cIdx) => {
                            const lineNum = Array.isArray(contextLine) ? contextLine[0] : cIdx + 1;
                            const code = Array.isArray(contextLine) ? contextLine[1] : contextLine;
                            const isCurrentLine = lineNum === frame.lineNo;

                            return (
                              <CodeLine key={cIdx} $highlighted={isCurrentLine}>
                                <LineNumber theme={theme}>{lineNum}</LineNumber>
                                <LineCode theme={theme}>{code}</LineCode>
                              </CodeLine>
                            );
                          })}
                        </FrameCode>
                      )}

                      {isExpanded && contextLines.length === 0 && (
                        <FrameCode theme={theme}>
                          <CodeLine $highlighted={false}>
                            <LineCode theme={theme} style={{ color: theme.colors.muted, fontStyle: 'italic' }}>
                              No source context available for this frame
                            </LineCode>
                          </CodeLine>
                        </FrameCode>
                      )}
                    </FrameContainer>
                  );
                })
              )}
            </Card>

            {/* Breadcrumbs Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Breadcrumbs</CardTitle>

              {breadcrumbs.length === 0 ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>
                    No breadcrumbs available
                  </ThemedText>
                </EmptyState>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <BreadcrumbTable theme={theme}>
                    <thead>
                      <tr>
                        <BreadcrumbHeaderCell theme={theme}>Timestamp</BreadcrumbHeaderCell>
                        <BreadcrumbHeaderCell theme={theme}>Category</BreadcrumbHeaderCell>
                        <BreadcrumbHeaderCell theme={theme}>Message</BreadcrumbHeaderCell>
                        <BreadcrumbHeaderCell theme={theme}>Level</BreadcrumbHeaderCell>
                      </tr>
                    </thead>
                    <tbody>
                      {breadcrumbs.map((bc, idx) => (
                        <BreadcrumbRow key={idx} theme={theme}>
                          <BreadcrumbCell theme={theme} style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '12px' }}>
                            {formatTime(bc.timestamp)}
                          </BreadcrumbCell>
                          <BreadcrumbCell theme={theme} style={{ fontWeight: 500 }}>
                            {bc.category || '-'}
                          </BreadcrumbCell>
                          <BreadcrumbCell theme={theme} style={{ maxWidth: '400px', wordBreak: 'break-word' }}>
                            {bc.message || bc.data?.message || bc.data?.url || '-'}
                          </BreadcrumbCell>
                          <BreadcrumbCell theme={theme}>
                            <LevelBadge $level={bc.level}>
                              {bc.level || 'info'}
                            </LevelBadge>
                          </BreadcrumbCell>
                        </BreadcrumbRow>
                      ))}
                    </tbody>
                  </BreadcrumbTable>
                </div>
              )}
            </Card>
          </MainContent>

          {/* ─── Sidebar ───────────────────────────────────────────────── */}
          <Sidebar theme={theme}>

            {/* Issue Info Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Issue Info</CardTitle>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Status</InfoLabel>
                <StatusBadge theme={theme} $status={issue.status}>
                  {issue.status || 'unknown'}
                </StatusBadge>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>First Seen</InfoLabel>
                <InfoValue theme={theme}>{formatDate(issue.firstSeen)}</InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Last Seen</InfoLabel>
                <InfoValue theme={theme}>{formatDate(issue.lastSeen)}</InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Events</InfoLabel>
                <InfoValue theme={theme}>{issue.count ?? 'N/A'}</InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Users Affected</InfoLabel>
                <InfoValue theme={theme}>{issue.userCount ?? 'N/A'}</InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Project</InfoLabel>
                <InfoValue theme={theme}>
                  {issue.project?.name || issue.project?.slug || 'N/A'}
                </InfoValue>
              </InfoRow>
            </Card>

            {/* Tags Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Tags</CardTitle>

              {tags.length === 0 ? (
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  No tags available
                </ThemedText>
              ) : (
                tags.map((tag, idx) => (
                  <TagRow key={idx} theme={theme}>
                    <TagKey theme={theme}>{tag.key}</TagKey>
                    <TagValue theme={theme}>{tag.value}</TagValue>
                  </TagRow>
                ))
              )}
            </Card>

            {/* Context Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Context</CardTitle>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Browser</InfoLabel>
                <InfoValue theme={theme}>
                  {contexts.browser
                    ? `${contexts.browser.name || ''}${contexts.browser.version ? ' ' + contexts.browser.version : ''}`.trim() || 'N/A'
                    : 'N/A'}
                </InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>OS</InfoLabel>
                <InfoValue theme={theme}>
                  {contexts.os
                    ? `${contexts.os.name || ''}${contexts.os.version ? ' ' + contexts.os.version : ''}`.trim() || 'N/A'
                    : 'N/A'}
                </InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Device</InfoLabel>
                <InfoValue theme={theme}>
                  {contexts.device?.model || 'N/A'}
                </InfoValue>
              </InfoRow>

              <InfoRow theme={theme}>
                <InfoLabel theme={theme}>Runtime</InfoLabel>
                <InfoValue theme={theme}>
                  {contexts.runtime
                    ? `${contexts.runtime.name || ''}${contexts.runtime.version ? ' ' + contexts.runtime.version : ''}`.trim() || 'N/A'
                    : 'N/A'}
                </InfoValue>
              </InfoRow>
            </Card>

            {/* Public URL Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Public URL</CardTitle>
              <PublicUrlContainer theme={theme}>
                <PublicUrlInput
                  theme={theme}
                  value={publicUrl}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <CopyButton
                  theme={theme}
                  $copied={publicUrlCopied}
                  onClick={handleCopyPublicUrl}
                >
                  {publicUrlCopied ? 'Copied!' : 'Copy'}
                </CopyButton>
              </PublicUrlContainer>
            </Card>

            {/* Actions Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Actions</CardTitle>

              <ActionsContainer theme={theme}>
                {issue.status !== 'resolved' && (
                  <ActionButton
                    theme={theme}
                    style={{ backgroundColor: `${StatusPalette.green}15`, color: StatusPalette.green, borderColor: `${StatusPalette.green}40` }}
                    onClick={() => handleUpdateStatus('resolved')}
                    disabled={!!updatingStatus}
                  >
                    {updatingStatus === 'resolved' ? 'Resolving...' : 'Resolve Issue'}
                  </ActionButton>
                )}

                {issue.status !== 'ignored' && (
                  <ActionButton
                    theme={theme}
                    style={{ backgroundColor: `${StatusPalette.amber}15`, color: StatusPalette.amber, borderColor: `${StatusPalette.amber}40` }}
                    onClick={() => handleUpdateStatus('ignored')}
                    disabled={!!updatingStatus}
                  >
                    {updatingStatus === 'ignored' ? 'Ignoring...' : 'Ignore Issue'}
                  </ActionButton>
                )}

                {(issue.status === 'resolved' || issue.status === 'ignored') && (
                  <ActionButton
                    theme={theme}
                    onClick={() => handleUpdateStatus('unresolved')}
                    disabled={!!updatingStatus}
                  >
                    {updatingStatus === 'unresolved' ? 'Unresolving...' : 'Unresolve Issue'}
                  </ActionButton>
                )}

                {issue.permalink && (
                  <ExternalLink
                    theme={theme}
                    href={issue.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Sentry &rarr;
                  </ExternalLink>
                )}

                <ActionButton
                  theme={theme}
                  $variant="primary"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                >
                  {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                </ActionButton>

                <ActionButton
                  theme={theme}
                  onClick={handleCreateTask}
                >
                  Create Task
                </ActionButton>

                {aiAnalysis && (
                  <ActionButton
                    theme={theme}
                    onClick={handleCreateTaskFromAnalysis}
                  >
                    Create Task from AI Analysis
                  </ActionButton>
                )}
              </ActionsContainer>

              {/* AI Analysis Results */}
              {aiAnalysis && (
                <AnalysisSection theme={theme}>
                  <AnalysisLabel theme={theme}>AI Analysis</AnalysisLabel>

                  {aiAnalysis.analysis && (
                    <AnalysisText theme={theme}>{aiAnalysis.analysis}</AnalysisText>
                  )}

                  {aiAnalysis.rootCause && (
                    <>
                      <AnalysisLabel theme={theme}>Root Cause</AnalysisLabel>
                      <AnalysisText theme={theme}>{aiAnalysis.rootCause}</AnalysisText>
                    </>
                  )}

                  {aiAnalysis.suggestedFix && (
                    <>
                      <AnalysisLabel theme={theme}>Suggested Fix</AnalysisLabel>
                      <AnalysisText theme={theme}>{aiAnalysis.suggestedFix}</AnalysisText>
                    </>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {aiAnalysis.suggestedPriority != null && (
                      <AnalysisBadge theme={theme} $color={StatusPalette.amber}>
                        Priority: {aiAnalysis.suggestedPriority}
                      </AnalysisBadge>
                    )}
                    {aiAnalysis.suggestedType != null && (
                      <AnalysisBadge theme={theme} $color="#3b82f6">
                        Type: {aiAnalysis.suggestedType}
                      </AnalysisBadge>
                    )}
                  </div>
                </AnalysisSection>
              )}
            </Card>
          </Sidebar>
        </ContentGrid>
      </PageContainer>
    </Layout>
  );
};

export { SentryIssueDetailPage };
export default SentryIssueDetailPage;
